import { Container } from '@/components';
import Pagination from '@/components/Pagination';
import { SUPPORTED_LOCALES } from '@/constants';
import type { DirectoryType, IDirectoryItem } from '@/fetchers/contentfulAPI/directory.fetchers';
import { useDebounce, useRouteChangePending } from '@/hooks';
import {
  DIRECTORY_COPY,
  DIRECTORY_SEARCH_MIN_LENGTH,
  DIRECTORY_SHARED_COPY,
  buildDirectoryPath,
  formatDirectoryCount,
  getLocaleDisplayName,
  getSlugLocale,
} from '@/utils/directoryPage.utils';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  DirectoryCardBody,
  DirectoryCardLink,
  DirectoryCardLogo,
  DirectoryCardTitle,
  DirectoryDescription,
  DirectoryGrid,
  DirectoryTitle,
  DirectoryToolbar,
  DirectoryWrapper,
  LanguageBadge,
  LocaleFilterButton,
  LocaleFilterChevron,
  LocaleFilterList,
  LocaleFilterOption,
  LocaleFilterWrapper,
  NoResultsText,
  ResultCount,
  SearchIcon,
  SearchInput,
  SearchInputWrapper,
} from './Directory.styles';

const DEFAULT_CARRIER_LOGO = '/images/default-carrier-logo.svg';

export interface DirectoryPageProps {
  type: DirectoryType;
  items: IDirectoryItem[];
  total: number;
  currentPage: number;
  totalPages: number;
  locale?: string;
  /** The term `getServerSideProps` actually applied, i.e. a validated `?q=`. */
  searchTerm?: string;
}

/**
 * Every carrier logo in master-data lives on assets.parcelperform.com, which is already an
 * allowed remotePattern, so next/image can optimise them. It still needs the onError swap:
 * an entry without a curated carrierInformation logo falls back to carrierLogo, which a sync
 * script fills by matching our slugs to carrier ids fuzzily, so it can carry a URL that no
 * longer resolves.
 */
const CarrierLogo: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [logoSrc, setLogoSrc] = useState(src);

  useEffect(() => {
    setLogoSrc(src);
  }, [src]);

  return <Image src={logoSrc} alt={alt} width={40} height={40} onError={() => setLogoSrc(DEFAULT_CARRIER_LOGO)} />;
};

const DirectoryCard: React.FC<{ item: IDirectoryItem; showLogo: boolean; showLanguageBadge: boolean }> = ({
  item,
  showLogo,
  showLanguageBadge,
}) => {
  const itemLocale = getSlugLocale(item.slug);

  return (
    <li>
      <DirectoryCardLink href={`/${item.slug}`}>
        {showLogo && (
          <DirectoryCardLogo>
            <CarrierLogo src={item.carrierLogo || DEFAULT_CARRIER_LOGO} alt={item.title} />
          </DirectoryCardLogo>
        )}
        <DirectoryCardBody>
          <DirectoryCardTitle>{item.title}</DirectoryCardTitle>
          {showLanguageBadge && itemLocale && <LanguageBadge>{getLocaleDisplayName(itemLocale)}</LanguageBadge>}
        </DirectoryCardBody>
      </DirectoryCardLink>
    </li>
  );
};

const DirectoryPage: React.FC<DirectoryPageProps> = ({
  type,
  items,
  total,
  currentPage,
  totalPages,
  locale,
  searchTerm: appliedSearchTerm,
}) => {
  const isCarriersDirectory = type === 'carriers';
  const { title, description, searchPlaceholder } = DIRECTORY_COPY[type];

  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState(appliedSearchTerm ?? '');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());
  // What this component last asked the URL to hold. Compared against instead of
  // `router.query.q` so that a URL change on its own — Back, Forward, a locale link — can
  // never be mistaken for a keystroke and bounce the user straight back out of it.
  const requestedSearchTermRef = useRef(appliedSearchTerm);

  const [isLocaleListOpen, setIsLocaleListOpen] = useState(false);
  const localeFilterRef = useRef<HTMLDivElement>(null);
  const localeFilterButtonRef = useRef<HTMLButtonElement>(null);
  const isNavigating = useRouteChangePending();

  // The URL is the single source of truth for what is on screen: typing rewrites it and
  // getServerSideProps re-runs with the term, which is what makes a search paginate like any
  // other view. Two deliberate choices here:
  //  - `replace`, not `push`, so Back does not have to walk "d" → "dh" → "dhl".
  //  - `page` is dropped, because a narrower result set has fewer pages and
  //    createDirectoryGetServerSideProps 404s on page > totalPages.
  useEffect(() => {
    const nextSearchTerm = debouncedSearchTerm.length >= DIRECTORY_SEARCH_MIN_LENGTH ? debouncedSearchTerm : undefined;

    if (nextSearchTerm === requestedSearchTermRef.current) return;

    requestedSearchTermRef.current = nextSearchTerm;
    router.replace(buildDirectoryPath(type, { locale, searchTerm: nextSearchTerm }), undefined, { scroll: false });
  }, [debouncedSearchTerm, type, locale, router]);

  useEffect(() => {
    if (!isLocaleListOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!localeFilterRef.current?.contains(event.target as Node)) {
        setIsLocaleListOpen(false);
      }
    };

    // Escape closes and hands focus back to the trigger, so keyboard users are not
    // stranded inside a 23-item list they can only leave by tabbing through it.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setIsLocaleListOpen(false);
      localeFilterButtonRef.current?.focus();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLocaleListOpen]);

  const isSearching = !!appliedSearchTerm;
  // Only the all-languages view mixes locales; once filtered, every badge would read the same.
  const showLanguageBadge = !locale;

  return (
    <Container>
      <DirectoryWrapper>
        <DirectoryTitle>{title}</DirectoryTitle>
        <DirectoryDescription>{description}</DirectoryDescription>

        <DirectoryToolbar>
          <SearchInputWrapper>
            <SearchIcon />
            <SearchInput
              type='search'
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </SearchInputWrapper>

          <LocaleFilterWrapper ref={localeFilterRef}>
            <LocaleFilterButton
              ref={localeFilterButtonRef}
              type='button'
              aria-haspopup='menu'
              aria-expanded={isLocaleListOpen}
              onClick={() => setIsLocaleListOpen((isOpen) => !isOpen)}
            >
              {locale ? getLocaleDisplayName(locale) : DIRECTORY_SHARED_COPY.allLocales}
              <LocaleFilterChevron isOpen={isLocaleListOpen} />
            </LocaleFilterButton>
            {isLocaleListOpen && (
              <LocaleFilterList role='menu' aria-label={DIRECTORY_SHARED_COPY.localeFilterLabel}>
                {/* Selecting an option navigates client-side, so this component is never
                    remounted — the list has to close itself on pick. The term is carried over
                    so switching language narrows the current search instead of discarding it. */}
                <li role='presentation'>
                  <LocaleFilterOption
                    role='menuitem'
                    href={buildDirectoryPath(type, { searchTerm: appliedSearchTerm })}
                    isActive={!locale}
                    onClick={() => setIsLocaleListOpen(false)}
                  >
                    {DIRECTORY_SHARED_COPY.allLocales}
                  </LocaleFilterOption>
                </li>
                {SUPPORTED_LOCALES.map((supportedLocale) => (
                  <li key={supportedLocale} role='presentation'>
                    <LocaleFilterOption
                      role='menuitem'
                      href={buildDirectoryPath(type, { locale: supportedLocale, searchTerm: appliedSearchTerm })}
                      isActive={locale === supportedLocale}
                      onClick={() => setIsLocaleListOpen(false)}
                    >
                      {getLocaleDisplayName(supportedLocale)}
                    </LocaleFilterOption>
                  </li>
                ))}
              </LocaleFilterList>
            )}
          </LocaleFilterWrapper>
        </DirectoryToolbar>

        {/* aria-live: the count is the only confirmation a screen reader gets that a
            debounced search actually returned something. */}
        <ResultCount aria-live='polite'>{formatDirectoryCount({ type, total, isSearching })}</ResultCount>

        {items.length > 0 ? (
          <DirectoryGrid isPending={isNavigating}>
            {items.map((item) => (
              <DirectoryCard
                key={item.slug}
                item={item}
                showLogo={isCarriersDirectory}
                showLanguageBadge={showLanguageBadge}
              />
            ))}
          </DirectoryGrid>
        ) : (
          <NoResultsText>{DIRECTORY_SHARED_COPY.noResults}</NoResultsText>
        )}

        {/* A search paginates like any other view, but its pages must not become crawl
            surface: ?q= is unbounded, so nothing should invite a bot to walk page 2..N of a
            term it invented. The page itself is noindex — see the directory page components. */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          isNofollow={isSearching}
          buildPageHref={(page) => buildDirectoryPath(type, { page, locale, searchTerm: appliedSearchTerm })}
        />
      </DirectoryWrapper>
    </Container>
  );
};

export default DirectoryPage;
